const STORAGE_KEY = "vd_first_touch_attribution";

const TRACKING_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "msclkid",
  "fbclid",
] as const;

export type LeadAttribution = Partial<Record<(typeof TRACKING_KEYS)[number], string>> & {
  landing_page: string;
  referrer: string;
  captured_at: string;
};

const safeParse = (value: string | null): LeadAttribution | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as LeadAttribution;
    return parsed?.landing_page && parsed?.captured_at ? parsed : null;
  } catch {
    return null;
  }
};

export const captureFirstTouchAttribution = (): LeadAttribution | null => {
  if (typeof window === "undefined") return null;

  const existing = safeParse(window.localStorage.getItem(STORAGE_KEY));
  if (existing) return existing;

  const params = new URLSearchParams(window.location.search);
  const attribution: LeadAttribution = {
    landing_page: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || "direct",
    captured_at: new Date().toISOString(),
  };

  TRACKING_KEYS.forEach((key) => {
    const value = params.get(key)?.trim();
    if (value) attribution[key] = value.slice(0, 250);
  });

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Attribution should never block navigation or lead submission.
  }

  return attribution;
};

export const getLeadAttribution = (): LeadAttribution | null => {
  if (typeof window === "undefined") return null;
  return safeParse(window.localStorage.getItem(STORAGE_KEY)) || captureFirstTouchAttribution();
};

export const attributionSummary = (attribution = getLeadAttribution()): string => {
  if (!attribution) return "First touch unavailable";

  const channel = attribution.utm_source
    ? [attribution.utm_source, attribution.utm_medium].filter(Boolean).join(" / ")
    : attribution.gclid
      ? "Google Ads"
      : attribution.msclkid
        ? "Microsoft Ads"
        : attribution.fbclid
          ? "Meta"
          : attribution.referrer === "direct"
            ? "Direct"
            : attribution.referrer;

  return [
    `First touch: ${channel}`,
    attribution.utm_campaign ? `Campaign: ${attribution.utm_campaign}` : null,
    `Landing: ${attribution.landing_page}`,
  ].filter(Boolean).join(" | ");
};

export const analyticsAttributionParams = (
  attribution = getLeadAttribution()
): Record<string, string> => {
  if (!attribution) return {};

  const params: Record<string, string> = {
    first_touch_landing_page: attribution.landing_page,
    first_touch_referrer: attribution.referrer,
    first_touch_captured_at: attribution.captured_at,
  };

  TRACKING_KEYS.forEach((key) => {
    const value = attribution[key];
    if (value) params[`first_${key}`] = value;
  });

  if (attribution.utm_source) {
    params.first_touch_channel = [attribution.utm_source, attribution.utm_medium].filter(Boolean).join(" / ");
  } else if (attribution.gclid) {
    params.first_touch_channel = "Google Ads";
  } else if (attribution.msclkid) {
    params.first_touch_channel = "Microsoft Ads";
  } else if (attribution.fbclid) {
    params.first_touch_channel = "Meta";
  } else {
    params.first_touch_channel = attribution.referrer === "direct" ? "Direct" : attribution.referrer;
  }

  return params;
};

export const attributedSourceUrl = (): string => {
  if (typeof window === "undefined") return "";
  const attribution = getLeadAttribution();
  const url = new URL(window.location.href);
  if (attribution?.landing_page) url.searchParams.set("first_touch", attribution.landing_page);
  if (attribution?.utm_source) url.searchParams.set("first_source", attribution.utm_source);
  if (attribution?.utm_medium) url.searchParams.set("first_medium", attribution.utm_medium);
  if (attribution?.utm_campaign) url.searchParams.set("first_campaign", attribution.utm_campaign);
  if (attribution?.utm_term) url.searchParams.set("first_term", attribution.utm_term);
  if (attribution?.utm_content) url.searchParams.set("first_content", attribution.utm_content);
  if (attribution?.gclid) url.searchParams.set("first_gclid", attribution.gclid);
  if (attribution?.msclkid) url.searchParams.set("first_msclkid", attribution.msclkid);
  if (attribution?.fbclid) url.searchParams.set("first_fbclid", attribution.fbclid);
  return url.toString();
};

export const sourceUrlAttributionLabel = (sourceUrl: string | null): string | null => {
  if (!sourceUrl) return null;

  try {
    const url = new URL(sourceUrl, "https://valentinedigitalmarketing.com");
    const source = url.searchParams.get("first_source");
    const medium = url.searchParams.get("first_medium");
    const campaign = url.searchParams.get("first_campaign");

    if (source) {
      const channel = [source, medium].filter(Boolean).join(" / ");
      return campaign ? `${channel} - ${campaign}` : channel;
    }

    if (url.searchParams.get("first_gclid")) return "Google Ads";
    if (url.searchParams.get("first_msclkid")) return "Microsoft Ads";
    if (url.searchParams.get("first_fbclid")) return "Meta";
    if (url.searchParams.get("first_touch")) return "Direct / organic";
  } catch {
    return null;
  }

  return null;
};
